import { Box, Snackbar, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Page frame. Reading views stay on the 1280px content column; `fluid` lets
 * the pipeline board take the whole viewport width so its columns can breathe.
 */
export const PageRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fluid',
})<{ fluid?: boolean }>(({ theme, fluid }) => {
  const { layout, space } = theme.palette.tokens;
  return {
    display: 'flex',
    flexDirection: 'column',
    padding: `${space[8]}px ${layout.contentGutter}px ${space[14]}px`,
    maxWidth: fluid
      ? 'none'
      : `${layout.contentMax + layout.contentGutter * 2}px`,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    ...(fluid && {
      // Bounded, not a minimum: the pipeline columns scroll on their own and
      // their infinite loading is triggered by that inner scroll.
      height: '100vh',
      paddingBottom: space[6],
    }),
  };
});

/** Controls that live on the title row: view toggle + search. */
export const HeaderTools = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.palette.tokens.space[3],
  flexWrap: 'wrap',
}));

export const PlaceholderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.palette.tokens.space[3],
  minHeight: '400px',
  padding: theme.palette.tokens.space[6],
  textAlign: 'center',
  color: theme.palette.text.secondary,
  '& .material-symbols-outlined': {
    fontSize: '24px',
    color: theme.palette.text.disabled,
  },
}));

export const PipelineToolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  minHeight: '24px',
  marginBottom: theme.palette.tokens.space[1],
}));

export const PipelineContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'stretch',
  gap: theme.palette.tokens.space[4],
  padding: `${theme.palette.tokens.space[1]}px 0 ${theme.palette.tokens.space[4]}px`,
  flex: 1,
  minHeight: '480px',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.tokens.color.n300,
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
}));

export const StyledToast = styled(Snackbar)(({ theme }) => ({
  '.MuiSnackbarContent-root': {
    minWidth: '280px',
    maxWidth: '360px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: `${theme.palette.tokens.radius.card}px`,
    boxShadow: theme.palette.tokens.shadow.float,
    ...theme.typography.body2,
  },
}));

export const ToastTypography = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  ...theme.typography.body2,
  color: theme.palette.text.primary,
  '& span': { color: theme.palette.tokens.color.positive, fontSize: '18px' },
}));
